import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { PhoneHealthListItem } from '../../../services/phoneNumberHealthService';
import { HealthStatusBadge, UsageTypeBadge, CarrierIssueBadge, WarmupStatusBadge } from './StatusBadges';
import UtilizationBar from './UtilizationBar';
import TestCallModal from './TestCallModal';
import TelnyxBadge from './TelnyxBadge';

interface Props {
  item: PhoneHealthListItem | null;
  periodLabel?: string;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
  onUnlock?: (id: string) => void;
}

const HEALTH_STATUSES = [
  { value: 'healthy', label: 'Healthy' },
  { value: 'warning', label: 'Warning' },
  { value: 'replace', label: 'Replace' },
  { value: 'warm_up', label: 'Warm-Up' },
  { value: 'quarantine', label: 'Quarantine' },
  { value: 'retired', label: 'Retired' },
  { value: 'out_of_inventory', label: 'Out of Inventory' },
];

const Stat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-lg border p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold mt-0.5">{value}</p>
  </div>
);

const PhoneNumberDetailDrawer: React.FC<Props> = ({
  item,
  periodLabel = 'Today',
  onClose,
  onStatusChange,
  onUnlock,
}) => {
  const [testCallOpen, setTestCallOpen] = React.useState(false);
  const [unlocking, setUnlocking] = React.useState(false);

  if (!item) return null;

  const connectPct = item.connectRate != null ? `${(item.connectRate * 100).toFixed(1)}%` : '—';
  const deliveryPct = item.deliveryRate != null ? `${(item.deliveryRate * 100).toFixed(1)}%` : '—';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-card border-l shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold font-mono">{item.phoneNumber}</h2>
            <div className="mt-1.5 flex items-center gap-2">
              <TelnyxBadge />
              <span className="text-xs text-muted-foreground">{item.connectionName ?? item.provider}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-muted">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <div className="flex flex-wrap gap-2 items-center">
            <HealthStatusBadge status={item.healthStatus} since={item.healthStatusSince} />
            <UsageTypeBadge label={item.purposeLabel} />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Health Status</label>
            <select
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-background"
              value={item.healthStatus}
              onChange={(e) => onStatusChange(item.id, e.target.value)}
            >
              {HEALTH_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {item.healthStatusManual && (
              <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                <p className="font-medium">Manual override locked</p>
                <p className="text-muted-foreground mt-0.5">
                  Auto reclassify after{' '}
                  {item.healthManualCallsRemaining != null
                    ? `${item.healthManualCallsRemaining} more calls`
                    : '100 more calls'}
                  .
                </p>
                {onUnlock && (
                  <button
                    type="button"
                    disabled={unlocking}
                    className="mt-2 text-xs font-medium text-primary underline disabled:opacity-50"
                    onClick={async () => {
                      setUnlocking(true);
                      try {
                        await onUnlock(item.id);
                      } finally {
                        setUnlocking(false);
                      }
                    }}
                  >
                    {unlocking ? 'Unlocking…' : 'Unlock now (allow auto recalculation)'}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Stat label="Area Code" value={item.areaCode ?? '—'} />
            <Stat label="Timezone" value={item.timezone ?? 'Unknown'} />
            <Stat label="Connected Rate" value={connectPct} />
            <Stat label="Delivery Rate" value={deliveryPct} />
            <Stat label="Calls (30d)" value={`${item.callsAnswered}/${item.callsMade}`} />
            <Stat label="Texts (30d)" value={`${item.textsDelivered}/${item.textsSent}`} />
          </div>

          <div className="rounded-lg border p-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Utilization ({periodLabel.toLowerCase()})
            </p>
            <UtilizationBar
              used={item.callsToday}
              capacity={item.effectiveDailyCallLimit}
              percent={item.utilizationTodayPercent}
            />
            <p className="text-[10px] text-muted-foreground mt-2">
              Daily cap from health status · resets each ET day
            </p>
          </div>

          {item.healthStatus === 'warm_up' && (
            <div className="rounded-lg border p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Warm-Up Progress</p>
              <WarmupStatusBadge
                warmupDay={item.warmupDay}
                callsToday={item.callsToday}
                dailyCallLimit={item.dailyCallLimit}
                textsToday={item.textsToday}
                dailyTextLimit={item.dailyTextLimit}
                healthStatus={item.healthStatus}
              />
            </div>
          )}

          {item.carrierIssues.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Carrier Issues</p>
              <CarrierIssueBadge issues={item.carrierIssues} />
            </div>
          )}

          {item.campaigns.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Campaigns</p>
              <ul className="text-sm space-y-1">
                {item.campaigns.map((c) => (
                  <li key={c.id} className="text-foreground">{c.name}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <p>SMS capable: {item.smsCapable ? 'Yes' : 'No'}</p>
            <p>Volume multiplier: {item.volumeMultiplier}x</p>
            {item.lastUsedAt && (
              <p>Last used: {new Date(item.lastUsedAt).toLocaleString()}</p>
            )}
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <button
            type="button"
            onClick={() => setTestCallOpen(true)}
            className="flex-1 px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted"
          >
            Test Call
          </button>
          <button
            type="button"
            onClick={() => onStatusChange(item.id, 'replace')}
            className="flex-1 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
          >
            Mark Replace
          </button>
        </div>
      </aside>

      {testCallOpen && (
        <TestCallModal fromNumber={item.phoneNumber} onClose={() => setTestCallOpen(false)} />
      )}
    </>
  );
};

export default PhoneNumberDetailDrawer;
